import { DateTime } from 'luxon';

/**
 * Spec 013 P7 (FR-036): words for the card name checks admin page. Plain
 * functions, tested in `cardNameChecks.test.ts`.
 */

const DATE_TIME = 'MMM d, yyyy, h:mm a';

/** Trip start and end in the listing's zone, e.g. "Oct 27, 2026, 10:00 AM to Oct 29, 2026, 10:00 AM MST". */
export function tripDatesText(
  startAt: number | null | undefined,
  endAt: number | null | undefined,
  timezone: string | null | undefined,
): string {
  if (startAt == null && endAt == null) return 'No dates';
  const zone = timezone || 'local';
  const at = (ms: number) => {
    const value = DateTime.fromMillis(ms).setZone(zone);
    return (value.isValid ? value : DateTime.fromMillis(ms)).toFormat(DATE_TIME);
  };
  const zoneName = DateTime.fromMillis((startAt ?? endAt) as number)
    .setZone(zone)
    .toFormat('ZZZZ');
  const start = startAt == null ? 'Not set' : at(startAt);
  const end = endAt == null ? 'Not set' : at(endAt);
  return timezone && zoneName ? `${start} to ${end} ${zoneName}` : `${start} to ${end}`;
}

/** The name on the card and its last four digits. */
export function cardText(
  cardholderName: string | null | undefined,
  cardLast4: string | null | undefined,
): string {
  const name = cardholderName?.trim() || 'No name on the card';
  return cardLast4 ? `${name} •••• ${cardLast4}` : name;
}

/** The name the card was compared with, and where it came from. */
export function comparedWithText(
  accountName: string | null | undefined,
  accountNameSource: string | null | undefined,
): string {
  const name = accountName?.trim() || 'No name on the account';
  return accountNameSource === 'dl' ? `${name} (verified licence)` : name;
}

const PURPOSE_LABELS: Record<string, string> = {
  checkout: 'Booking',
  extension: 'Extension',
  invoice: 'Post-trip charge',
};

export function purposeText(purpose: string): string {
  return PURPOSE_LABELS[purpose] ?? purpose;
}

/** When the host and admins were told; null while the card has not paid. */
export function notifiedText(notifiedAt: number | null | undefined): string {
  if (notifiedAt == null) return 'Not yet paid';
  return DateTime.fromMillis(notifiedAt).toFormat(DATE_TIME);
}

/** The trip id filter from the page URL (`?bookingId=`), or null. */
export function tripIdFilter(search: URLSearchParams): string | null {
  return search.get('bookingId')?.trim() || null;
}

/** The page URL's search params with the trip id filter set or cleared. */
export function withTripIdFilter(search: URLSearchParams, tripId: string): URLSearchParams {
  const next = new URLSearchParams(search);
  const trimmed = tripId.trim();
  if (trimmed) next.set('bookingId', trimmed);
  else next.delete('bookingId');
  return next;
}
